/**
 * Diálogos de **Control Efectivo**.
 *
 * Cuatro piezas, las mismas del panel anterior: las **bóvedas** de una ficha, sus
 * **depósitos y egresos** (con el formulario en línea para registrar unos y
 * otros), y los dos asistentes para crear la ficha —por **totales de turno** y
 * por **bóvedas detalladas**—.
 */

import { CheckSquare, Layers, ListChecks, Square, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { ArchivoInput } from '@/components/ui/archivo-input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CampoFecha } from '@/components/ui/calendario';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { cn, formatCurrency, formatDateValue } from '@/lib/utils';

import {
  useBorrarMovimientoFicha,
  useBovedasFicha,
  useCrearFichaEfectivo,
  useDepositosFicha,
  usePendientesEfectivo,
  useRegistrarMovimientoFicha,
} from '../control-efectivo.api';
import type {
  BovedaPendiente,
  CuentaEfectivo,
  DetalleFichaEfectivo,
  FichaEfectivo,
  TurnoPendiente,
} from '../control-efectivo.types';

const CONTROL =
  'h-10 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

const ACEPTADOS = 'application/pdf,image/png,image/jpeg,application/vnd.ms-excel';

// ---------------------------------------------------------------------------
// Bóvedas de una ficha
// ---------------------------------------------------------------------------

interface DialogoBovedasProps {
  abierto: boolean;
  ficha: FichaEfectivo | null;
  onCerrar: () => void;
}

/** Las bóvedas que componen una ficha, tal como se depositaron. */
export function DialogoBovedasFicha({ abierto, ficha, onCerrar }: DialogoBovedasProps) {
  const consulta = useBovedasFicha(abierto ? ficha?.id ?? null : null);

  return (
    <Dialog
      open={abierto}
      onClose={onCerrar}
      title={`Bóvedas de la ficha #${ficha?.id ?? ''}`}
      description={
        ficha?.tipo === 'detallado'
          ? 'Bóvedas individuales que se procesaron en esta ficha.'
          : 'Totales por turno que se procesaron en esta ficha.'
      }
      size="lg"
      footer={
        <Button variant="outline" size="sm" onClick={onCerrar}>
          Cerrar
        </Button>
      }
    >
      {consulta.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, fila) => (
            <Skeleton key={fila} className="h-8 w-full" />
          ))}
        </div>
      ) : null}

      {consulta.isError ? (
        <Alert variant="error" title="No se pudieron leer las bóvedas">
          Vuelve a intentarlo en unos segundos.
        </Alert>
      ) : null}

      {consulta.data ? (
        <div className="space-y-3">
          <div className="overflow-auto rounded-card border border-ink-100">
            <table className="w-full border-collapse text-[13px]">
              <thead className="bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                  <th className="px-2 py-2">#</th>
                  <th className="px-2 py-2">Fecha</th>
                  <th className="px-2 py-2">Turno</th>
                  <th className="px-2 py-2">Empleado</th>
                  <th className="px-2 py-2 text-right">Monto</th>
                </tr>
              </thead>
              <tbody>
                {consulta.data.bovedas.map((boveda) => (
                  <tr key={`${boveda.id}-${boveda.turno_id}`} className="border-t border-ink-100">
                    <td className="px-2 py-2 text-ink-500 tabular-nums">{boveda.id}</td>
                    <td className="px-2 py-2 whitespace-nowrap">
                      {boveda.fecha ? formatDateValue(boveda.fecha) : '—'}
                    </td>
                    <td className="px-2 py-2 tabular-nums">{boveda.turno_id ?? '—'}</td>
                    <td className="px-2 py-2">{boveda.empleado || '—'}</td>
                    <td className="px-2 py-2 text-right font-semibold tabular-nums">
                      {formatCurrency(boveda.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-ink-50/60 font-semibold">
                <tr className="border-t border-ink-100">
                  <td className="px-2 py-2 text-right" colSpan={4}>
                    Total de la ficha
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums">
                    {formatCurrency(consulta.data.total)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <p className="text-xs text-ink-500">
            {consulta.data.bovedas.length.toLocaleString('es-PE')} bóveda(s)
            {consulta.data.modo === 'foto'
              ? ' · importes tal como se procesaron (la ficha guarda su foto)'
              : ''}
          </p>
        </div>
      ) : null}
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Depósitos y egresos de una ficha
// ---------------------------------------------------------------------------

interface DialogoDepositosProps {
  abierto: boolean;
  ficha: FichaEfectivo | null;
  onCerrar: () => void;
  onAviso: (mensaje: string) => void;
}

type FormularioAbierto = 'ninguno' | 'deposito' | 'egreso';

/** Los depósitos y egresos de la ficha, con su formulario en línea. */
export function DialogoDepositosFicha({
  abierto,
  ficha,
  onCerrar,
  onAviso,
}: DialogoDepositosProps) {
  const consulta = useDepositosFicha(abierto ? ficha?.id ?? null : null);
  const registrar = useRegistrarMovimientoFicha();
  const borrar = useBorrarMovimientoFicha(ficha?.id ?? 0);

  const [formulario, setFormulario] = useState<FormularioAbierto>('ninguno');
  const [fecha, setFecha] = useState('');
  const [monto, setMonto] = useState('');
  const [referencia, setReferencia] = useState('');
  const [cuenta, setCuenta] = useState('');
  const [archivo, setArchivo] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const datos = consulta.data;
  const hoy = datos?.hoy ?? '';
  const pendiente = datos?.totales.pendiente ?? 0;
  const cuentas: CuentaEfectivo[] = datos?.cuentas ?? [];

  const puedeRegistrar = datos
    ? formulario === 'deposito'
      ? datos.permisos.agregar_deposito
      : datos.permisos.agregar_egreso
    : false;

  const limpiar = () => {
    setMonto('');
    setReferencia('');
    setCuenta('');
    setArchivo(null);
    setError(null);
  };

  const guardar = () => {
    if (!ficha) return;

    const valor = Number(monto.replace(',', '.')) || 0;
    const esEgreso = formulario === 'egreso';

    if (!(valor > 0)) {
      setError('El monto debe ser mayor a cero.');

      return;
    }

    if (valor > pendiente) {
      setError(
        esEgreso
          ? 'El monto del egreso no debe ser mayor al pendiente por depositar.'
          : 'El monto del depósito no debe ser mayor al pendiente por depositar.',
      );

      return;
    }

    if (!esEgreso && !cuenta) {
      setError('Debe elegir la cuenta bancaria de destino.');

      return;
    }

    setError(null);

    registrar.mutate(
      {
        id: ficha.id,
        esEgreso,
        datos: {
          fecha: fecha || hoy,
          monto: valor.toFixed(2),
          referencia: referencia.trim(),
          cuenta_id: esEgreso ? undefined : cuenta,
          archivo,
        },
      },
      {
        onSuccess: (respuesta) => {
          onAviso(respuesta.message);
          setFormulario('ninguno');
          limpiar();
        },
        onError: (fallo) => {
          setError(fallo instanceof Error ? fallo.message : 'No se pudo registrar el movimiento.');
        },
      },
    );
  };

  const abrirComprobante = async (ruta: string, nombre: string) => {
    setError(null);

    try {
      await abrirPdfAutenticado(ruta, { nombre });
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se pudo abrir el comprobante.');
    }
  };

  return (
    <Dialog
      open={abierto}
      onClose={() => {
        setFormulario('ninguno');
        limpiar();
        onCerrar();
      }}
      title={`Depósitos de la ficha #${ficha?.id ?? ''}`}
      description={
        formulario === 'deposito'
          ? 'Registra un depósito a la cuenta bancaria elegida.'
          : formulario === 'egreso'
            ? 'Registra un egreso del efectivo de esta ficha.'
            : 'Lo que se ha depositado al banco y lo que se ha egresado de esta ficha.'
      }
      size="lg"
      footer={
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setFormulario('ninguno');
            limpiar();
            onCerrar();
          }}
        >
          Cerrar
        </Button>
      }
    >
      {consulta.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, fila) => (
            <Skeleton key={fila} className="h-8 w-full" />
          ))}
        </div>
      ) : null}

      {consulta.isError ? (
        <Alert variant="error" title="No se pudieron leer los depósitos">
          Vuelve a intentarlo en unos segundos.
        </Alert>
      ) : null}

      {error ? <Alert variant="error">{error}</Alert> : null}

      {datos && formulario === 'ninguno' ? (
        <div className="space-y-3">
          {datos.depositos.length === 0 ? (
            <Alert variant="info">Esta ficha todavía no tiene depósitos ni egresos.</Alert>
          ) : (
            <div className="overflow-auto rounded-card border border-ink-100">
              <table className="w-full border-collapse text-[13px]">
                <thead className="bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-2 py-2">#</th>
                    <th className="px-2 py-2">Fecha</th>
                    <th className="px-2 py-2">Destino</th>
                    <th className="px-2 py-2">Referencia</th>
                    <th className="px-2 py-2 text-center">Comprobante</th>
                    <th className="px-2 py-2 text-right">Monto</th>
                    <th className="px-2 py-2 text-right" />
                  </tr>
                </thead>
                <tbody>
                  {datos.depositos.map((movimiento) => (
                    <tr key={movimiento.id} className="border-t border-ink-100">
                      <td className="px-2 py-2 text-ink-500 tabular-nums">{movimiento.id}</td>
                      <td className="px-2 py-2 whitespace-nowrap">
                        {movimiento.fecha ? formatDateValue(movimiento.fecha) : '—'}
                      </td>
                      <td className="px-2 py-2">
                        <Badge variant={movimiento.es_egreso ? 'danger' : 'brand'}>
                          {movimiento.es_egreso ? 'Egreso' : 'Depósito'}
                        </Badge>
                        <p className="mt-0.5 text-[11px] text-ink-500">{movimiento.destino}</p>
                      </td>
                      <td className="max-w-[12rem] px-2 py-2">
                        <span className="block truncate" title={movimiento.referencia ?? ''}>
                          {movimiento.referencia || '—'}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-center">
                        {movimiento.comprobante ? (
                          <button
                            type="button"
                            className="text-brand-600 hover:text-brand-700"
                            title="Ver el comprobante"
                            aria-label={`Ver el comprobante del movimiento ${movimiento.id}`}
                            onClick={() =>
                              void abrirComprobante(
                                movimiento.comprobante as string,
                                movimiento.archivo ?? 'comprobante',
                              )
                            }
                          >
                            Ver
                          </button>
                        ) : (
                          <span className="text-ink-300">—</span>
                        )}
                      </td>
                      <td
                        className={cn(
                          'px-2 py-2 text-right font-semibold whitespace-nowrap tabular-nums',
                          movimiento.es_egreso ? 'text-rose-600' : 'text-emerald-700',
                        )}
                      >
                        {formatCurrency(Math.abs(movimiento.monto))}
                      </td>
                      <td className="px-2 py-2 text-right">
                        {datos.permisos.eliminar_movimiento ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Eliminar el movimiento"
                            aria-label={`Eliminar el movimiento ${movimiento.id}`}
                            loading={borrar.isPending && borrar.variables === movimiento.id}
                            onClick={() => {
                              setError(null);
                              borrar.mutate(movimiento.id, {
                                onSuccess: (respuesta) => onAviso(respuesta.message),
                                onError: (fallo) =>
                                  setError(
                                    fallo instanceof Error
                                      ? fallo.message
                                      : 'No se pudo eliminar el movimiento.',
                                  ),
                              });
                            }}
                          >
                            <Trash2 className="size-4 text-rose-600" aria-hidden="true" />
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="grid gap-2 rounded-card border border-ink-100 bg-ink-50/50 p-3 text-sm sm:grid-cols-2">
            <p className="flex justify-between gap-2">
              <span className="text-ink-500">Total a depositar</span>
              <strong className="tabular-nums">{formatCurrency(datos.totales.a_depositar)}</strong>
            </p>
            <p className="flex justify-between gap-2">
              <span className="text-ink-500">Total depositado</span>
              <strong className="tabular-nums text-emerald-700">
                {formatCurrency(datos.totales.depositado)}
              </strong>
            </p>
            <p className="flex justify-between gap-2">
              <span className="text-ink-500">Total egresado</span>
              <strong className="tabular-nums text-rose-600">
                {formatCurrency(datos.totales.egresado)}
              </strong>
            </p>
            <p className="flex justify-between gap-2">
              <span className="text-ink-500">Pendiente de depósito</span>
              <strong className="tabular-nums text-amber-700">
                {formatCurrency(datos.totales.pendiente)}
              </strong>
            </p>
          </div>

          {pendiente > 0 ? (
            <div className="flex flex-wrap items-center gap-2">
              {datos.permisos.agregar_deposito ? (
                <Button
                  size="sm"
                  onClick={() => {
                    limpiar();
                    setFecha(hoy);
                    setFormulario('deposito');
                  }}
                >
                  Depósito a cuenta
                </Button>
              ) : null}
              {datos.permisos.agregar_egreso ? (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => {
                    limpiar();
                    setFecha(hoy);
                    setFormulario('egreso');
                  }}
                >
                  Egreso
                </Button>
              ) : null}
            </div>
          ) : (
            <Alert variant="success">La ficha está depositada por completo.</Alert>
          )}
        </div>
      ) : null}

      {datos && formulario !== 'ninguno' ? (
        <div className="flex flex-col gap-4">
          <Alert variant="info">
            Pendiente por depositar: <strong>{formatCurrency(pendiente)}</strong>
          </Alert>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-ink-700" htmlFor="ficha-fecha">
                Fecha
              </label>
              <CampoFecha
                id="ficha-fecha"
                tipo="date"
                value={fecha}
                onChange={(evento) => setFecha(evento.target.value)}
                aria-label="Fecha del movimiento"
              />
            </div>

            <Input
              label="Monto (S/)"
              type="number"
              step="0.01"
              min="0.01"
              value={monto}
              hint={`Máximo ${formatCurrency(pendiente)}`}
              onChange={(evento) => setMonto(evento.target.value)}
            />

            {formulario === 'deposito' ? (
              <>
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-ink-700" htmlFor="ficha-cuenta">
                    Cuenta de destino
                  </label>
                  <Select
                    id="ficha-cuenta"
                    value={cuenta}
                    onChange={(evento) => setCuenta(evento.target.value)}
                    className={CONTROL}
                    aria-label="Cuenta de destino"
                  >
                    <option value="">Seleccionar cuenta…</option>
                    {cuentas.map((item) => (
                      <option key={item.id} value={String(item.id)}>
                        {item.etiqueta}
                      </option>
                    ))}
                  </Select>
                </div>

                <Input
                  label="Referencia"
                  value={referencia}
                  placeholder="Nro. de voucher, operación…"
                  onChange={(evento) => setReferencia(evento.target.value)}
                />
              </>
            ) : (
              <>
                <Input
                  label="Motivo"
                  value={referencia}
                  placeholder="Ej: movilidad, mantenimiento…"
                  onChange={(evento) => setReferencia(evento.target.value)}
                />
                <Input label="Método de pago" value="Efectivo" readOnly />
              </>
            )}
          </div>

          <ArchivoInput
            label="Comprobante (opcional)"
            accept={ACEPTADOS}
            nombre={archivo?.name ?? null}
            hint="PDF, PNG, JPG o Excel de hasta 10 MB. Lo guarda la API y se sirve con la sesión."
            onSeleccionar={(elegido) => setArchivo(elegido?.archivo ?? null)}
          />

          <div className="flex flex-wrap justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setFormulario('ninguno');
                limpiar();
              }}
            >
              Cancelar
            </Button>
            <Button
              size="sm"
              variant={formulario === 'egreso' ? 'danger' : 'primary'}
              loading={registrar.isPending}
              disabled={!puedeRegistrar}
              onClick={guardar}
            >
              {formulario === 'egreso' ? 'Registrar egreso' : 'Registrar depósito'}
            </Button>
          </div>
        </div>
      ) : null}
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Asistentes para crear la ficha
// ---------------------------------------------------------------------------

interface Seleccion {
  id: number;
  amount: number;
}

interface DialogoDepositarTurnoProps {
  abierto: boolean;
  onCerrar: () => void;
  onProcesado: (mensaje: string) => void;
}

/** Depositar agrupando las bóvedas pendientes por totales de turno. */
export function DialogoDepositarTurno({
  abierto,
  onCerrar,
  onProcesado,
}: DialogoDepositarTurnoProps) {
  const consulta = usePendientesEfectivo('turno', '', '', abierto);
  const crear = useCrearFichaEfectivo();
  const [seleccion, setSeleccion] = useState<Seleccion[]>([]);
  const [error, setError] = useState<string | null>(null);

  const turnos: TurnoPendiente[] = consulta.data?.turnos ?? [];
  const totalPendiente = consulta.data?.total ?? 0;
  const elegidos = useMemo(() => new Set(seleccion.map((item) => item.id)), [seleccion]);
  const total = seleccion.reduce((suma, item) => suma + item.amount, 0);

  const alternar = (turno: TurnoPendiente) => {
    setSeleccion((actual) =>
      actual.some((item) => item.id === turno.turno_id)
        ? actual.filter((item) => item.id !== turno.turno_id)
        : [...actual, { id: turno.turno_id, amount: turno.total }],
    );
  };

  const procesar = () => {
    if (total <= 0) {
      setError('No puede enviarse a procesar un depósito de S/ 0.00.');

      return;
    }

    setError(null);
    crear.mutate(
      { modo: 'turno', monto: total, detalles: seleccion },
      {
        onSuccess: (respuesta) => {
          onProcesado(`${respuesta.message} Importe: ${formatCurrency(total)}.`);
          setSeleccion([]);
        },
        onError: (fallo) =>
          setError(fallo instanceof Error ? fallo.message : 'No se pudo procesar el depósito.'),
      },
    );
  };

  return (
    <Dialog
      open={abierto}
      onClose={() => {
        setSeleccion([]);
        setError(null);
        onCerrar();
      }}
      title="Bóvedas por turno pendientes de depositar"
      description="Elige los turnos cuyo efectivo va a entrar en la ficha de depósito."
      size="lg"
      footer={
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setSeleccion([]);
              setError(null);
              onCerrar();
            }}
          >
            Cancelar
          </Button>
          <Button size="sm" loading={crear.isPending} onClick={procesar}>
            <Layers className="size-4" aria-hidden="true" />
            Procesar
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error ? <Alert variant="error">{error}</Alert> : null}

        {consulta.isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, fila) => (
              <Skeleton key={fila} className="h-8 w-full" />
            ))}
          </div>
        ) : null}

        {turnos.length === 0 && !consulta.isLoading ? (
          <Alert variant="info">No hay bóvedas pendientes de depositar.</Alert>
        ) : null}

        {turnos.length > 0 ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                leftIcon={<CheckSquare className="size-4" aria-hidden="true" />}
                onClick={() =>
                  setSeleccion(turnos.map((turno) => ({ id: turno.turno_id, amount: turno.total })))
                }
              >
                Seleccionar todo
              </Button>
              <Button
                variant="outline"
                size="sm"
                leftIcon={<Square className="size-4" aria-hidden="true" />}
                onClick={() => setSeleccion([])}
              >
                Deseleccionar
              </Button>
              <span className="ml-auto text-xs text-ink-500">
                {turnos.length.toLocaleString('es-PE')} turno(s) pendiente(s)
              </span>
            </div>

            <div className="max-h-[22rem] overflow-auto rounded-card border border-ink-100">
              <table className="w-full border-collapse text-[13px]">
                <thead className="sticky top-0 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-2 py-2">Turno</th>
                    <th className="px-2 py-2">Fecha</th>
                    <th className="px-2 py-2">Empleado</th>
                    <th className="px-2 py-2 text-right">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {turnos.map((turno) => (
                    <tr
                      key={turno.turno_id}
                      className={cn(
                        'border-t border-ink-100',
                        elegidos.has(turno.turno_id) && 'bg-brand-50/60',
                      )}
                    >
                      <td className="px-2 py-2">
                        <Checkbox
                          label={`#${turno.turno_id}`}
                          checked={elegidos.has(turno.turno_id)}
                          onChange={() => alternar(turno)}
                        />
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap">
                        {turno.hora_llegada ? formatDateValue(turno.hora_llegada.slice(0, 10)) : '—'}
                      </td>
                      <td className="px-2 py-2">{turno.empleado}</td>
                      <td className="px-2 py-2 text-right font-semibold tabular-nums">
                        {formatCurrency(turno.total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="grid gap-2 rounded-card border border-ink-100 bg-ink-50/50 p-3 text-sm sm:grid-cols-2">
              <p className="flex justify-between gap-2">
                <span className="text-ink-500">Total pendiente por depositar</span>
                <strong className="tabular-nums">{formatCurrency(totalPendiente)}</strong>
              </p>
              <p className="flex justify-between gap-2">
                <span className="text-ink-500">Selección a procesar</span>
                <strong className="tabular-nums text-brand-700">{formatCurrency(total)}</strong>
              </p>
            </div>
          </>
        ) : null}
      </div>
    </Dialog>
  );
}

interface DialogoDepositarDetalladoProps {
  abierto: boolean;
  onCerrar: () => void;
  onProcesado: (mensaje: string) => void;
}

/** Depositar eligiendo bóvedas individuales, con filtro de fechas. */
export function DialogoDepositarDetallado({
  abierto,
  onCerrar,
  onProcesado,
}: DialogoDepositarDetalladoProps) {
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const consulta = usePendientesEfectivo('detallado', desde, hasta, abierto);
  const crear = useCrearFichaEfectivo();
  const [seleccion, setSeleccion] = useState<Seleccion[]>([]);
  const [error, setError] = useState<string | null>(null);

  const bovedas: BovedaPendiente[] = consulta.data?.bovedas ?? [];
  const totalPendiente = consulta.data?.total ?? 0;
  const elegidas = useMemo(() => new Set(seleccion.map((item) => item.id)), [seleccion]);
  const total = seleccion.reduce((suma, item) => suma + item.amount, 0);

  const alternar = (boveda: BovedaPendiente) => {
    setSeleccion((actual) =>
      actual.some((item) => item.id === boveda.id)
        ? actual.filter((item) => item.id !== boveda.id)
        : [...actual, { id: boveda.id, amount: boveda.total }],
    );
  };

  const procesar = () => {
    if (total <= 0) {
      setError('No puede enviarse a procesar un depósito de S/ 0.00.');

      return;
    }

    setError(null);
    crear.mutate(
      { modo: 'detallado', monto: total, detalles: seleccion },
      {
        onSuccess: (respuesta) => {
          onProcesado(`${respuesta.message} Importe: ${formatCurrency(total)}.`);
          setSeleccion([]);
        },
        onError: (fallo) =>
          setError(fallo instanceof Error ? fallo.message : 'No se pudo procesar el depósito.'),
      },
    );
  };

  return (
    <Dialog
      open={abierto}
      onClose={() => {
        setSeleccion([]);
        setError(null);
        onCerrar();
      }}
      title="Bóvedas individuales para depositar"
      description="Elige las bóvedas concretas que van a entrar en la ficha de depósito."
      size="lg"
      footer={
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setSeleccion([]);
              setError(null);
              onCerrar();
            }}
          >
            Cancelar
          </Button>
          <Button size="sm" loading={crear.isPending} onClick={procesar}>
            <ListChecks className="size-4" aria-hidden="true" />
            Procesar
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error ? <Alert variant="error">{error}</Alert> : null}

        <div className="grid gap-3 sm:grid-cols-3">
          <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
            Fecha desde
            <CampoFecha
              tipo="date"
              value={desde}
              max={hasta || undefined}
              onChange={(evento) => setDesde(evento.target.value)}
              aria-label="Fecha desde"
            />
          </label>

          <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
            Fecha hasta
            <CampoFecha
              tipo="date"
              value={hasta}
              min={desde || undefined}
              onChange={(evento) => setHasta(evento.target.value)}
              aria-label="Fecha hasta"
            />
          </label>

          <div className="flex items-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setDesde('');
                setHasta('');
              }}
            >
              Limpiar filtro
            </Button>
          </div>
        </div>

        {consulta.isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, fila) => (
              <Skeleton key={fila} className="h-8 w-full" />
            ))}
          </div>
        ) : null}

        {bovedas.length === 0 && !consulta.isLoading ? (
          <Alert variant="info">No hay bóvedas pendientes con ese filtro.</Alert>
        ) : null}

        {bovedas.length > 0 ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                leftIcon={<CheckSquare className="size-4" aria-hidden="true" />}
                onClick={() =>
                  setSeleccion(bovedas.map((boveda) => ({ id: boveda.id, amount: boveda.total })))
                }
              >
                Seleccionar todo lo filtrado
              </Button>
              <Button
                variant="outline"
                size="sm"
                leftIcon={<Square className="size-4" aria-hidden="true" />}
                onClick={() => setSeleccion([])}
              >
                Deseleccionar
              </Button>
              <span className="ml-auto text-xs text-ink-500">
                {bovedas.length.toLocaleString('es-PE')} bóveda(s)
              </span>
            </div>

            <div className="max-h-[22rem] overflow-auto rounded-card border border-ink-100">
              <table className="w-full border-collapse text-[13px]">
                <thead className="sticky top-0 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-2 py-2">#</th>
                    <th className="px-2 py-2">Fecha</th>
                    <th className="px-2 py-2">Turno</th>
                    <th className="px-2 py-2">Empleado</th>
                    <th className="px-2 py-2 text-right">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {bovedas.map((boveda) => (
                    <tr
                      key={boveda.id}
                      className={cn(
                        'border-t border-ink-100',
                        elegidas.has(boveda.id) && 'bg-brand-50/60',
                      )}
                    >
                      <td className="px-2 py-2">
                        <Checkbox
                          label={`#${boveda.id}`}
                          checked={elegidas.has(boveda.id)}
                          onChange={() => alternar(boveda)}
                        />
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap">
                        {boveda.fecha ? formatDateValue(boveda.fecha) : '—'}
                      </td>
                      <td className="px-2 py-2 tabular-nums">{boveda.turno_id}</td>
                      <td className="px-2 py-2">{boveda.empleado}</td>
                      <td className="px-2 py-2 text-right font-semibold tabular-nums">
                        {formatCurrency(boveda.total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="grid gap-2 rounded-card border border-ink-100 bg-ink-50/50 p-3 text-sm sm:grid-cols-2">
              <p className="flex justify-between gap-2">
                <span className="text-ink-500">Total pendiente por depositar</span>
                <strong className="tabular-nums">{formatCurrency(totalPendiente)}</strong>
              </p>
              <p className="flex justify-between gap-2">
                <span className="text-ink-500">Selección a procesar</span>
                <strong className="tabular-nums text-brand-700">{formatCurrency(total)}</strong>
              </p>
            </div>
          </>
        ) : null}
      </div>
    </Dialog>
  );
}

/** Reexporta el tipo del detalle para el que arma la ficha desde la página. */
export type { DetalleFichaEfectivo };
